import { InteractionStatus } from "@azure/msal-browser";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import { useState, useEffect, useMemo } from "react";
import { useMatomo } from "@datapunt/matomo-tracker-react";
import { loginRequest } from "../auth.config";
import axiosInstance from "../axiosInstanceSetup";
import { useAuth } from "../context/AuthContext";
import { useLocation } from "react-router-dom";

function AuthGuard({ children }) {
  const { instance, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const { pushInstruction } = useMatomo();
  const location = useLocation();
  const { authUser, setAuthUser, authReady, setAuthReady } = useAuth();
  const [authError, setAuthError] = useState(null);

  const account = useMemo(
    () => instance.getActiveAccount() || instance.getAllAccounts()[0],
    [instance]
  );
  const isEmbedRoute = location.pathname.startsWith("/embed");
  // Login redirect
  useEffect(() => {
    if (isEmbedRoute) return;
    if (inProgress === InteractionStatus.None && !isAuthenticated) {
      instance.loginRedirect(loginRequest);
    }
  }, [inProgress, isAuthenticated, instance]);

  // HYDRATE AUTH ONCE
  useEffect(() => {
    if (!isAuthenticated || !account || authReady) return;

    const hydrateAuth = async () => {
      const userId = account.username;

      try {
        sessionStorage.setItem(
          "bellNotification",
          JSON.stringify([])
        );
        localStorage.setItem("idToken", account.idToken);
        localStorage.setItem("userName", account.name);
        localStorage.setItem("userId", userId);

        // Matomo
        pushInstruction("setUserId", userId);
        pushInstruction("setCustomDimension", 1, account.name);

        let role = null;
        let internalUserId = null;
        // Role API (may fail)
        try {
          const { data } = await axiosInstance.get(
            `${process.env.REACT_APP_API_URL}/user/id/${userId}`
          );

          role = data.role_name;
          internalUserId = data.user_id;

          localStorage.setItem("role", role);
          localStorage.setItem("user_id", internalUserId);
        } catch (roleErr) {
          console.warn("Role API failed, continuing without role", roleErr);
        }

        // ALWAYS set context
        setAuthUser({
          userId,
          name: account.name,
          role,               // may be null
          internalUserId,     // may be null
        });
        setAuthReady(true);
      } catch (err) {
        console.error("Auth initialization failed", err);
        setAuthError(true);
      }
    };

    hydrateAuth();
  }, [
    isAuthenticated,
    account,
    authReady,
    setAuthUser,
    setAuthReady,
    pushInstruction,
  ]);

  // if localStorage not found
  useEffect(() => {
    if (!authReady || !account) return;

    const userId = localStorage.getItem("userId");
    if (!userId) {
      // restore from MSAL
      localStorage.setItem("userId", account.username);
      localStorage.setItem("userName", account.name);
      localStorage.setItem("idToken", account.idToken);

      // role - restore from context
      if (authUser) {
        localStorage.setItem("role", authUser.role);
        localStorage.setItem("user_id", authUser.internalUserId);
      }
    }
  }, [authReady, account, authUser, location]);

  if (authError) {
    return <div>Authenticaion error. Please try login again.</div>;
  }

  if (!isEmbedRoute) {
    if (inProgress !== InteractionStatus.None || !isAuthenticated || !authReady) {
      return <div>Loading...</div>;
    }
  }

  return <>{children}</>;
}

export default AuthGuard;
